import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types"
import React from "react"
import ReactPlayer from "react-player/lazy"
import { css } from "styled-components"
import { getArticleFeatureFromType } from "../../molecules/article-feature"
import BlockQuote from "../../molecules/block-quote"
import { CodeBlock } from "../../molecules/code-block"
import { IContentfulArticleFeature } from "../../types/blog.types"
import VariantContainer from "../variant-container"
import {
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Hyperlink,
  Italic,
  ListItem,
  Paragraph,
  StaticImage,
  Underline,
} from "./blog-rich-text.style"

/** Options mapping for RTF -> Blog Styled JSX */
const BlogRichTextOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
    [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
    [MARKS.UNDERLINE]: (text) => <Underline>{text}</Underline>,
    [MARKS.CODE]: (text) => <Code>{text}</Code>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      if (
        node.content.length === 1 &&
        node.content[0].nodeType === "text" &&
        node.content[0].value.startsWith(`<iframe`)
      ) {
        // if we spot an iframe, parse and render it
        return (
          <div dangerouslySetInnerHTML={{ __html: node.content[0].value }} />
        )
      }
      return <Paragraph>{children}</Paragraph>
    },
    [BLOCKS.HEADING_1]: (node, children) => <Heading1>{children}</Heading1>,
    [BLOCKS.HEADING_2]: (node, children) => <Heading2>{children}</Heading2>,
    [BLOCKS.HEADING_3]: (node, children) => <Heading3>{children}</Heading3>,
    [BLOCKS.HEADING_4]: (node, children) => <Heading4>{children}</Heading4>,
    [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
      const { file = { url: "", contentType: "" }, description = "" } =
        node?.data?.target || {}
      const type = file.contentType.split("/")?.[0] || ""
      // If the asset is a video, render a ReactPlayer
      if (type === "video") {
        return (
          <ReactPlayer
            url={file.url}
            controls={true}
            height="100%"
            style={{ margin: "0 auto", maxWidth: "100%" }}
          />
        )
      }
      // If it's any other image type, render a static image
      return <StaticImage src={file.url} alt={description} />
    },
    [BLOCKS.LIST_ITEM]: (node, children) => {
      return <ListItem>{children}</ListItem>
    },
    [BLOCKS.QUOTE]: (node, children) => {
      return (
        <VariantContainer
          variants={[
            css`
              margin: 32px 0;
              ${Paragraph} {
                margin-bottom: 0;
              }
            `,
          ]}
        >
          <BlockQuote>{children}</BlockQuote>
        </VariantContainer>
      )
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const typename: string = node?.data?.target?.__typename || ""
      let entry = <></>
      switch (typename) {
        case "ContentfulArticleFeature":
          const {
            icon,
            iconColour: { hex },
            featureType,
            heading,
            copy,
            url,
            pdf,
          }: IContentfulArticleFeature = node?.data?.target || {}
          const ArticleFeatureComponent = getArticleFeatureFromType(featureType)
          entry = (
            <VariantContainer
              variants={[
                css`
                  margin: 32px 0;
                `,
              ]}
            >
              <ArticleFeatureComponent
                //@ts-ignore - icon will fallback to link if type incorrect
                icon={icon.toLowerCase()}
                colour={hex}
                title={heading}
                body={copy}
                to={pdf ? pdf.file.url : url}
              />
            </VariantContainer>
          )
          break
        case "ContentfulCodeBlock":
          const { code, language, settings } = node?.data?.target || {
            code: { code: "" },
            language: "",
            settings: [],
          }
          const props =
            settings?.length > 0 &&
            Object.assign({}, ...settings.map((x) => ({ [x.key]: x.value })))
          entry = (
            <CodeBlock
              children={code.code}
              language={language.toLowerCase()}
              {...props}
            />
          )
        default:
          break
      }
      return entry
    },
    [INLINES.HYPERLINK]: (node, children) => {
      if (
        node?.content[0]?.value?.includes("youtube.com") ||
        node?.content[0]?.value?.includes("vimeo.com")
      ) {
        // Simple video embed if it's a youtube or vimeo link
        return (
          <ReactPlayer
            url={node?.data?.uri || ""}
            controls={true}
            style={{ margin: "0 auto" }}
          />
        )
      }

      return (
        <Hyperlink
          href={node?.data?.uri || ""}
          target="_blank"
          rel="noreferrer"
        >
          {children}
        </Hyperlink>
      )
    },
  },
}

export { BlogRichTextOptions }
